import type { ImageGalleryImage } from "@/components/common/gallery";
import type { SUPType } from "@/types/sup";
import { s3Url } from "@/utils/s3-prefix";
import { sortMediaList } from "@/utils/sup/sort-media-list";
import { toCdn } from "@/utils/to-cdn";

const projectGeneric = "/images/home/hot-projects/project-generic.svg";

interface PrepareGalleryImagesProps {
	coverImage?: string;
	mediaList?: { url: string; name?: string; space?: string; type?: string; tags?: string[] }[];
	projectId?: string;
	type?: SUPType;
	shouldPrioritizeCoverImage?: boolean;
}

// Performance optimization: Avoid using new URL() and try-catch for checking absolute URLs
// This runs in a loop for every image in list views, so avoiding object allocation matters.
const isAbsoluteUrl = (u: string) => {
	// Simple regex check for http:// or https:// (case insensitive)
	return /^https?:\/\//i.test(u);
};

const resolveRelative = (image: string, type?: SUPType, projectId?: string) =>
	type === "unit" || image.startsWith("units/") ? s3Url(image) : s3Url(`projects/${projectId}/images/${image}`);

export function getProcessedImageUrl(image: string, type?: SUPType, projectId?: string): string {
	const raw = (image ?? "").trim();
	if (!raw) {
		return projectGeneric;
	}
	// Handle protocol-relative URLs as absolute
	if (raw.startsWith("//")) {
		return toCdn(`https:${raw}`);
	}
	// Absolute http/https URLs
	if (isAbsoluteUrl(raw)) {
		return toCdn(raw);
	}
	// Normalize relative paths by removing leading slashes
	const normalized = raw.replace(/^\/+/, "");
	return resolveRelative(normalized, type, projectId);
}

/**
 * Derive VTT captions URL from a video URL by replacing the extension.
 */
function getVideoCaptionsUrl(videoUrl: string): string | undefined {
	return videoUrl.replace(/\.mp4$/, ".vtt");
}

export function prepareGalleryImages({
	coverImage,
	mediaList,
	projectId,
	type,
	shouldPrioritizeCoverImage = true,
}: PrepareGalleryImagesProps): ImageGalleryImage[] {
	const imageList: ImageGalleryImage[] = [];

	// Filter out hidden items and separate videos from images
	const visibleMedia = mediaList?.filter(item => !item.tags?.includes("hidden")) || [];
	const videos = visibleMedia.filter(item => item.type === "video");
	const images = visibleMedia.filter(item => item.type !== "video");

	// Prepend videos as the first slides
	for (const video of videos) {
		const captionsUrl = getVideoCaptionsUrl(video.url);
		imageList.push({
			url: video.url,
			alt: "Video",
			name: "Video",
			space: "videos",
			isVideo: true,
			videoCaptionsSrc: captionsUrl,
			showCaptionsByDefault: !!captionsUrl,
		});
	}

	// Add cover image if it exists
	if (coverImage) {
		const coverImageUrl = getProcessedImageUrl(coverImage, type, projectId);
		imageList.push({
			url: coverImageUrl,
			alt: "Cover image",
			name: "",
			priority: shouldPrioritizeCoverImage, // Cover image gets priority loading
		});
	}

	// Add mediaList images (excluding videos, already handled above)
	if (images.length > 0) {
		imageList.push(
			...sortMediaList(images).map((item, index) => ({
				url: getProcessedImageUrl(item.url, type, projectId),
				alt: item.name || "Project image",
				name: item.name || "Others",
				space: item.space || "others",
				priority: !coverImage && index === 0 && shouldPrioritizeCoverImage, // First media item gets priority if no cover image
			})),
		);
	}

	// Fallback if no images
	if (imageList.length === 0) {
		imageList.push({
			url: projectGeneric,
			alt: "Default project image",
			name: "",
			space: "",
			priority: true, // Default image gets priority loading
		});
	}

	return imageList;
}
